import { Inject, Injectable } from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import {
    TIMERECORDING_ENTRY_VALIDITY,
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TimeRecordingEntryModel,
} from '../../models/complex/time-recording-entry.model';
import {
    isTimeRecordingGeneralEntryModel,
    TimeRecordingGeneralEntryModel,
} from '../../models/complex/time-recording-general-entry.model';
import {
    TIMERECORDING_VALIDATION_SEVERITY,
    TimeRecordingValidationResultModel,
} from '../../models/complex/time-recording-validation-result.model';
import { TimesheetEntryInterface } from '../../models/complex/timesheet-entry.interface';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { TimesheetModel } from '../../models/low-level/timesheet.model';
import { ValueHelperInstanceToken, ValueHelperInterface } from '../../interfaces/value-helper.interface';
import { TIMERECORDING_VALIDITY } from '../../types/timerecording-validity';
import { TimeUtilities } from '../../utilities/time-utilities';
import { TimeRecordingService } from '../time-recording/time-recording.service';
import { TimesheetAndExpensesInstanceToken, TimesheetAndExpensesInterface } from './timesheet-and-expenses.interface';
import { isNormalEntry } from '../../utilities/timesheet-entry.utils';

@Injectable({
    providedIn: 'root',
})
export class TimeRecordingValidationService {
    public constructor(
        private readonly translate: TranslateService,
        private readonly timeRecordingService: TimeRecordingService,
        @Inject(TimesheetAndExpensesInstanceToken)
        private readonly timesheetAndExpensesService: TimesheetAndExpensesInterface,
        @Inject(ValueHelperInstanceToken) private readonly valueHelperService: ValueHelperInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
    ) {}

    public performValidation(
        notification: NotificationIdentifierModel,
        timeRecordingEntries: TimeRecordingEntryModel[],
        otherTimeRecordingEntries: TimesheetEntryInterface[],
        currentTimesheet: Readonly<TimesheetModel>,
        submittedTimesheet: Readonly<TimesheetModel>,
        isRemoteSupport = false,
    ): TimeRecordingValidationResultModel[] {
        // if following check fails, we don't need to proceed the validation but can rather fail and return immediately
        const hardFailResult = this.checkForCorrectAmountOfEntries(
            timeRecordingEntries,
            <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_INVALIDNUMBEROFROWS'),
        );
        if (!hardFailResult[0].isValid) {
            return hardFailResult;
        }

        let validationResultArray: TimeRecordingValidationResultModel[] = [];

        validationResultArray = validationResultArray.concat(
            this.checkAllEntriesForValidTypeOfWorkCodes(
                timeRecordingEntries,
                <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_NOTYPEOFWORKSELECTED'),
            ),
        );
        validationResultArray = validationResultArray.concat(
            this.checkAllEntriesForDurationEqualOrExceeding48Hours(
                timeRecordingEntries,
                <string>(
                    this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_DURATION_EQUAL_OR_EXCEEDING_48_HOURS')
                ),
            ),
        );
        validationResultArray = validationResultArray.concat(
            this.checkOrder(
                timeRecordingEntries,
                false,
                this.clientManager.getDateTimeFormat(true),
                (endTime, startTime) =>
                    <string>(
                        this.translate.instant(
                            'CONFIRMATION_TIMERECORDING_VALIDATION_ENDTIME_AFTER_STARTTIME_OF_NEXT',
                            { endTime, startTime },
                        )
                    ),
            ),
        );
        validationResultArray = validationResultArray.concat(
            this.checkStartTimeBeforeEndTime(
                timeRecordingEntries,
                this.clientManager.getDateTimeFormat(true),
                (endTime, startTime) => <string>this.translate.instant(
                        'CONFIRMATION_TIMERECORDING_VALIDATION_ENDTIME_BEFORE_STARTTIME',
                        {
                            endTime,
                            startTime,
                        },
                    ),
            ),
        );
        validationResultArray = validationResultArray.concat(
            this.checkTRBKEndDateModelAndTimeModelIsValid(
                timeRecordingEntries,
                (model) => <string>this.translate.instant(
                        'CONFIRMATION_TIMERECORDING_VALIDATION_TRBK_ENDTIME_INVALID',
                        {
                            model,
                        },
                    ),
            ),
        );
        const canMultipleNotificationsBeInProgress = this.configService.getAllowNotificationPause();
        validationResultArray = validationResultArray.concat(
            this.checkForNonExistingOverlaps(
                timeRecordingEntries,
                <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_OVERLAPDETECTED'),
            ),
        );
        if (canMultipleNotificationsBeInProgress) {
            validationResultArray = validationResultArray.concat(
                this.checkForNonExistingsOverlapsWithInprogressNotifications(
                    timeRecordingEntries,
                    otherTimeRecordingEntries,
                    <string>(
                        this.translate.instant(
                            'CONFIRMATION_TIMERECORDING_VALIDATION_OTHER_TIMERECORDING_OVERLAPDETECTED',
                        )
                    ),
                ),
            );
        }
        if (!isRemoteSupport) {
            if (!canMultipleNotificationsBeInProgress) {
                validationResultArray = validationResultArray.concat(
                    this.checkForNonExistingGaps(
                        timeRecordingEntries,
                        <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_GAPDETECTED'),
                    ),
                );
            }
            validationResultArray = validationResultArray.concat(
                this.checkForCorrectAmountAndTypeOfEntriesInPhoneFixCase(
                    timeRecordingEntries,
                    <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_PHONEFIXTOOMANYROWS'),
                    this.configService.getEnableStandbyTravel(),
                    (entries) => this.timeRecordingService.getTravelToEntries(entries),
                    (entries) => this.timeRecordingService.getNonTravEntries(entries),
                    (entries, type) => this.timeRecordingService.countEntriesByType(entries, type),
                ),
            );
        }

        validationResultArray = validationResultArray.concat(
            this.checkForNonExistingsOverlapsWithTimesheet(
                notification,
                timeRecordingEntries,
                currentTimesheet,
                <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_TIMESHEETOVERLAPDETECTED'),
            ),
        );
        if (!this.configService.isTimesheetDisabled()) {
            const startOfWorkCurrentTimesheet = currentTimesheet.startOfWork;
            const endOfWorkLastSubmittedTimesheet = submittedTimesheet.getEndOfWork();
            validationResultArray = validationResultArray.concat(
                this.checkForStartDateTimesBeingBeforeStartOfWorkorEndOfWork(
                    timeRecordingEntries,
                    startOfWorkCurrentTimesheet,
                    endOfWorkLastSubmittedTimesheet,
                    <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_ENDOFWORKVIOLATED'),
                    <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_STARTOFWORKVIOLATED'),
                ),
            );
        }
        return validationResultArray;
    }

    // Time Recording Internal
    // Rules that can be checked within a time recording itself and dont have any dependency to other data.

    /**
     * If this rule returns a falsy result entry, the validation can stop right ahead and return falsy.
     * This rule checks if there is at least a single entry in the time recording. We should hard fail in case of a falsy
     * result of this rule to not SPAM the user with validation message that are obvious if there are no entries at all.
     * So making this function part of any subsequent rule is not a good idea.
     *
     * @param timeRecordingEntries time recording entries
     * @param errorMessage translated error message to use in the validation result
     * @returns array of validation result entries
     */
    public checkForCorrectAmountOfEntries(
        timeRecordingEntries: TimeRecordingEntryModel[],
        errorMessage: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        const validationEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_VALIDITY.INVALID_NUMBER_OF_ROWS,
            errorMessage,
            true,
            undefined,
        );
        validationResultArray.push(validationEntry);

        // no entries at all are never correct. We need at least a single entry for the phone fix case(NGMP58).
        if (!timeRecordingEntries || timeRecordingEntries.length < 1) {
            validationEntry.isValid = false;
        }

        return validationResultArray;
    }

    /**
     * Each time recording entry needs to have a "type of work" code selected.
     *
     * @param timeRecordingEntries time recording entries
     * @param errorMessage translated error message to use in the validation result
     * @returns array of validation result entries
     */
    public checkAllEntriesForValidTypeOfWorkCodes(
        timeRecordingEntries: TimeRecordingEntryModel[],
        errorMessage: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            if (currentEntry instanceof TimeRecordingGeneralEntryModel) {
                const validationResultEntry = new TimeRecordingValidationResultModel(
                    TIMERECORDING_ENTRY_VALIDITY.NO_TYPE_OF_WORK_SELECTED,
                    errorMessage,
                    true,
                    currentEntry.id,
                );
                validationResultArray.push(validationResultEntry);

                if (!currentEntry.typeOfWorkModel.selectedEntry) {
                    validationResultEntry.isValid = false;
                }
            }
        });

        return validationResultArray;
    }

    /**
     * An entry must not have a duration of 48 hours or more. Unlike checkDurationEqualorExceeding24Hours, which is
     * called directly to show just a warning, a duration this long is treated as a hard validation error so the
     * form cannot be submitted with it.
     *
     * @param timeRecordingEntries time recording entries
     * @param errorMessage translated error message to use in the validation result
     * @returns array of validation result entries
     */
    public checkAllEntriesForDurationEqualOrExceeding48Hours(
        timeRecordingEntries: TimeRecordingEntryModel[],
        errorMessage: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        const hoursPerTwoDays = 48;

        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_ENTRY_VALIDITY.DURATION_EQUAL_OR_EXCEEDING_48_HOURS,
                errorMessage,
                !this.checkDurationEqualOrExceedingHours(currentEntry, hoursPerTwoDays),
                currentEntry.id,
            );
            validationResultArray.push(validationResultEntry);
        });

        return validationResultArray;
    }

    /**
     * Validates that consecutive time recording entries do not overlap (each entry's end time must not be after the
     * next entry's start time). Returns one result per adjacent pair, with `isValid = false` on overlap.
     *
     * Special case — NGMP58 phone-fix / remote-work with a single entry: no pair exists to compare, so a pre-built
     * result marked `isValid = true` is returned to satisfy downstream consumers that expect a result object.
     *
     * @param timeRecordingEntries ordered list of entries to validate
     * @param isRemoteWork         whether the notification context is remote work (affects the NGMP58 single-entry branch)
     * @param dateTimeFormat       date-time format string used to format entry times in the message
     * @param formatMessage        produces the error message string given formatted end and start time strings
     * @returns validation results, one per adjacent pair (or one pre-built valid result for the NGMP58 single-entry case)
     */
    public checkOrder(
        timeRecordingEntries: TimeRecordingEntryModel[],
        isRemoteWork = false,
        dateTimeFormat: string,
        formatMessage: (endTime: string, startTime: string) => string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        // Phone Fix case
        if (timeRecordingEntries.length === 1) {
            const currentEntry = timeRecordingEntries[0];
            if (
                currentEntry &&
                isTimeRecordingGeneralEntryModel(currentEntry) &&
                (isRemoteWork || currentEntry.isPhoneFix())
            ) {
                const validationResultEntry = new TimeRecordingValidationResultModel(
                    TIMERECORDING_VALIDITY.INVALID_ORDER,
                    formatMessage(
                        currentEntry.endDateTime.format(dateTimeFormat),
                        currentEntry.startDateTime.format(dateTimeFormat),
                    ),
                    true,
                    currentEntry.id,
                );
                validationResultArray.push(validationResultEntry);
            }
        } else {
            for (let currentIndex = 0; currentIndex < timeRecordingEntries.length; currentIndex++) {
                const currentEntry = timeRecordingEntries[currentIndex];
                let nextEntry;
                if (currentIndex < timeRecordingEntries.length - 1) {
                    nextEntry = timeRecordingEntries[currentIndex + 1];
                }

                if (nextEntry) {
                    const validationResultEntry = new TimeRecordingValidationResultModel(
                        TIMERECORDING_VALIDITY.INVALID_ORDER,
                        formatMessage(
                            currentEntry.endDateTime.format(dateTimeFormat),
                            nextEntry.startDateTime.format(dateTimeFormat),
                        ),
                        true,
                        currentEntry.id,
                    );
                    validationResultArray.push(validationResultEntry);

                    if (currentEntry.endDateTime.isAfter(nextEntry.startDateTime)) {
                        validationResultEntry.isValid = false;
                    }
                }
            }
        }

        return validationResultArray;
    }

    /**
     * Check if TRBK end time
     *
     * @param timeRecordingEntries time recording entries
     * @param formatMessage produces the error message string given the string (empty string when valid)
     * @returns array of validation result entries
     */
    public checkTRBKEndDateModelAndTimeModelIsValid(
        timeRecordingEntries: TimeRecordingEntryModel[],
        formatMessage: (errMessage: string) => string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        // function name and showAtId both are related to TRBK/last entry but here we are checking all entries for incorrect end time
        // what should we correct? function name or the logic to check only TRBK entry for invalid end time?
        // function name and showAtId both are related to TRBK/last entry but here we are checking all entries for incorrect end time
        // what should we correct? function name or the logic to check only TRBK entry for invalid end time?
        const isEndTimeInvalid = timeRecordingEntries.some((entry) => !entry.endTimeModel.value.isValid());
        const errorMessage = isEndTimeInvalid ? 'End time' : '';
        const lastEntryId = timeRecordingEntries[timeRecordingEntries.length - 1].id;

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_VALIDITY.ENDTIME_BEFORE_STARTTIME,
            formatMessage(errorMessage),
            true,
            lastEntryId,
        );

        validationResultEntry.isValid = errorMessage.length === 0;
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    /**
     * An entry should not have an end time which is before its start time.
     *
     * @param timeRecordingEntries time recording entries
     * @param dateTimeFormat date-time format string used to format entry times in the message
     * @param formatMessage produces the error message string given formatted end and start time strings
     * @returns array of validation result entries
     */
    public checkStartTimeBeforeEndTime(
        timeRecordingEntries: TimeRecordingEntryModel[],
        dateTimeFormat: string,
        formatMessage: (endTime: string, startTime: string) => string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_VALIDITY.ENDTIME_BEFORE_STARTTIME,
                formatMessage(
                    currentEntry.endDateTime.format(dateTimeFormat),
                    currentEntry.startDateTime.format(dateTimeFormat),
                ),
                true,
                currentEntry.id,
            );
            validationResultArray.push(validationResultEntry);

            if (currentEntry.endTimeModel.value.isBefore(currentEntry.startDateTime)) {
                validationResultEntry.isValid = false;
            }
        });

        return validationResultArray;
    }

    /**
     * Gaps between entries are not permitted. This method is called recursively in case an entry has activities.
     *
     * @param timeRecordingEntries time recording entries
     * @param errorMessage translated error message to use in the validation result
     * @returns array of validation result entries
     */
    public checkForNonExistingGaps(
        timeRecordingEntries: TimeRecordingEntryModel[],
        errorMessage: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        let precedingEntry: TimeRecordingEntryModel | undefined;
        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_VALIDITY.GAP_DETECTED,
                errorMessage,
                true,
                currentEntry.id,
            );
            validationResultArray.push(validationResultEntry);

            if (precedingEntry) {
                if (currentEntry.startDateTime.isAfter(precedingEntry.endDateTime)) {
                    validationResultEntry.isValid = false;
                }
            }
            precedingEntry = currentEntry;
        });

        return validationResultArray;
    }

    /**
     * Entries should not overlap each other. This method is called recursively in case an entry has activities.
     *
     * @param timeRecordingEntries time recording entries
     * @param errorMessage translated error message to use in the validation result
     * @returns array of validation result entries
     */
    public checkForNonExistingOverlaps(
        timeRecordingEntries: TimeRecordingEntryModel[],
        errorMessage: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        let precedingEntry: TimeRecordingEntryModel | undefined;
        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_ENTRY_VALIDITY.OVERLAP_TIMERECORDING,
                errorMessage,
                true,
                currentEntry.id,
            );
            validationResultArray.push(validationResultEntry);

            if (precedingEntry) {
                if (
                    TimeUtilities.hasOverlap(
                        currentEntry.startDateTime,
                        currentEntry.endDateTime,
                        precedingEntry.startDateTime,
                        precedingEntry.endDateTime,
                    )
                ) {
                    validationResultEntry.isValid = false;
                }
            }
            precedingEntry = currentEntry;
        });

        return validationResultArray;
    }

    /**
     * In the "Phone Fix" case its not allowed to have additional rows except the TRTO and TRBK rows, beside the phone-fix-row.
     *
     * @param timeRecordingEntries time recording entries
     * @param errorMessage translated error message to use in the validation result
     * @param enableStandbyTravel whether standby travel is enabled
     * @param getTravelToEntries returns the travel-to entries from the given list
     * @param getNonTravEntries returns the non-travel entries from the given list
     * @param countEntriesByType returns the count of entries matching the given type code
     * @returns array of validation result entries
     */
    public checkForCorrectAmountAndTypeOfEntriesInPhoneFixCase(
        timeRecordingEntries: TimeRecordingEntryModel[],
        errorMessage: string,
        enableStandbyTravel: boolean,
        getTravelToEntries: (entries: TimeRecordingEntryModel[]) => TimeRecordingEntryModel[],
        getNonTravEntries: (entries: TimeRecordingEntryModel[]) => TimeRecordingEntryModel[],
        countEntriesByType: (entries: TimeRecordingEntryModel[], type: TIMERECORDING_TYPE_OF_WORK_CODES) => number,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_VALIDITY.PHONEFIX_TOO_MANY_ROWS,
            errorMessage,
            true,
            undefined,
        );
        validationResultArray.push(validationResultEntry);

        let phoneFixFlag = false;
        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            if (currentEntry instanceof TimeRecordingGeneralEntryModel) {
                // only overwrite the phone fix flag unless it hasnt been overwritten before
                if (!phoneFixFlag) {
                    phoneFixFlag = currentEntry.isPhoneFix();
                }
            }
        });

        if (phoneFixFlag) {
            if (timeRecordingEntries.length > 1) {
                validationResultEntry.isValid = false;
            }
            const maxTravelToStandbyEntries = 2;

            if (enableStandbyTravel) {
                const travelToEntries = getTravelToEntries(timeRecordingEntries);
                validationResultEntry.isValid =
                    travelToEntries.length >= 1 &&
                    travelToEntries.length <= maxTravelToStandbyEntries &&
                    getNonTravEntries(timeRecordingEntries).length === 1 &&
                    countEntriesByType(timeRecordingEntries, TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) === 1;
            }
        }

        return validationResultArray;
    }

    /// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    /// Time Recording External Rules
    /// Rules that need external data like End-Of-Work (EOW), Start-Of-Work (SOW) or other notifications.
    /// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    // No entry should be overlapping with any entry of another notification
    private checkForNonExistingsOverlapsWithTimesheet(
        notification: NotificationIdentifierModel,
        timeRecordingEntries: TimeRecordingEntryModel[],
        timesheet: TimesheetModel,
        errorMessage: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        let timesheetEntries = timesheet.getTimesheetEntries();

        timesheetEntries = timesheetEntries.filter((entry) => {
            // filter out normal entries for this notification; overlap is only calculated for other notifications
            if (isNormalEntry(entry)) {
                return !NotificationIdentifierModel.compare(entry.notificationId, notification);
            } else {
                // ... or manual entries
                return true;
            }
        });

        const isValid = true;
        // Check if any of the timesheet entries overlap with the current time recording
        timeRecordingEntries.forEach((timerecordingEntry) => {
            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_ENTRY_VALIDITY.OVERLAP_TIMESHEET,
                errorMessage,
                isValid,
                timerecordingEntry.id,
            );
            validationResultArray.push(validationResultEntry);

            // iterate over all timesheet entries to check for overlap. A for-loop is used here instead of a forEach
            // so that we can stop unnecessary loops execution by using 'break'
            for (const currentTimesheetEntry of timesheetEntries) {
                // check if an overlap is possible at all
                if (
                    TimeUtilities.hasOverlap(
                        timerecordingEntry.startDateTime,
                        timerecordingEntry.endDateTime,
                        currentTimesheetEntry.startTime.value,
                        currentTimesheetEntry.endTime.value,
                    )
                ) {
                    validationResultEntry.isValid = false;

                    // we can stop after finding the first overlap for this timerecording entry
                    break;
                }
            }
        });

        return validationResultArray;
    }

    // Checks whether any of the given time recording entries overlap with other in-progress notification's timerecording entries.
    // The other notifications are not confirmed yet, so an overlap is only a warning and does not invalidate the time recording.
    public checkForNonExistingsOverlapsWithInprogressNotifications(
        timeRecordingEntries: TimeRecordingEntryModel[],
        otherTimeRecordingEntries: TimesheetEntryInterface[],
        errorMessage: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        const isValid = true;
        // Check if any of the timesheet entries overlap with the current time recording
        timeRecordingEntries.forEach((timerecordingEntry) => {
            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_ENTRY_VALIDITY.OVERLAP_OTHER_TIMERECORDING,
                errorMessage,
                isValid,
                timerecordingEntry.id,
                TIMERECORDING_VALIDATION_SEVERITY.WARNING,
            );
            validationResultArray.push(validationResultEntry);

            // iterate over all otherTimeRecordingEntries to check for overlap. A for-loop is used here instead of a forEach
            // so that we can stop unnecessary loops execution by using 'break'
            for (const otherTRentry of otherTimeRecordingEntries) {
                // check if an overlap is possible at all
                if (
                    TimeUtilities.hasOverlap(
                        timerecordingEntry.startDateTime,
                        timerecordingEntry.endDateTime,
                        otherTRentry.startTime.value,
                        otherTRentry.endTime.value,
                    )
                ) {
                    validationResultEntry.isValid = false;
                    // Check: Do we want to break after first overlap or do we want to find all overlaps? If we see the comment above then we should stop after first overlap.
                }
            }
        });

        return validationResultArray;
    }

    /**
     * No entry should have a startDateTime value before Start-Of-Work (SOW). This methods searches for the entry with
     * the earliest startDateTime and compares it to SOW. The entry should not be before SOW.
     * It is possible to manually save SOW with a time which is before previous EOW using timesheet screen,
     * that is why it is required to also check for EOW time so as not to allow to submit an entry having a startDateTime before EOW time
     *
     * @param timeRecordingEntries time recording entries
     * @param startOfWorkCurrentTimesheet the start of work
     * @param endOfWorkLastSubmittedTimesheet the end of work
     * @param endOfWorkViolatedMessage translated error message for the end-of-work violation case
     * @param startOfWorkViolatedMessage translated error message for the start-of-work violation case
     * @returns array of validation result entries
     * @throws Error if no entry with minimum startDateTime is found
     */
    private checkForStartDateTimesBeingBeforeStartOfWorkorEndOfWork(
        timeRecordingEntries: TimeRecordingEntryModel[],
        startOfWorkCurrentTimesheet: moment.Moment,
        endOfWorkLastSubmittedTimesheet: moment.Moment,
        endOfWorkViolatedMessage: string,
        startOfWorkViolatedMessage: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        const entryWithMinimumStartDateTime = _.minBy(
            timeRecordingEntries,
            (currentTimeRecEntry: TimeRecordingEntryModel) => {
                return currentTimeRecEntry.startDateTime;
            },
        );

        if (!entryWithMinimumStartDateTime) {
            throw new Error(`Unable to find entry with minimum start date!`);
        }

        let isValid = true;
        let validationErrorMessage = '';
        if (
            endOfWorkLastSubmittedTimesheet.isValid() &&
            entryWithMinimumStartDateTime.startDateTime.isBefore(endOfWorkLastSubmittedTimesheet)
        ) {
            isValid = false;
            validationErrorMessage = endOfWorkViolatedMessage;
        } else if (
            entryWithMinimumStartDateTime &&
            entryWithMinimumStartDateTime.startDateTime.isBefore(startOfWorkCurrentTimesheet)
        ) {
            isValid = false;
            validationErrorMessage = startOfWorkViolatedMessage;
        }

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.START_OF_WORK_VIOLATED,
            validationErrorMessage,
            isValid,
            entryWithMinimumStartDateTime.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    /**
     * Called directly and not part of the common validation process as its purpose is to show just a warning.
     *
     * @param timeRecordingEntry time recording entry
     * @returns true if the entry has a duration of 24 hours or more
     */
    public checkDurationEqualorExceeding24Hours(timeRecordingEntry: TimeRecordingEntryModel): boolean {
        const hoursPerDay = 24;
        return this.checkDurationEqualOrExceedingHours(timeRecordingEntry, hoursPerDay);
    }

    public checkDurationEqualOrExceedingHours(timeRecordingEntry: TimeRecordingEntryModel, hours: number): boolean {
        return timeRecordingEntry.endDateTime.diff(timeRecordingEntry.startDateTime, 'hours') >= hours;
    }

    /**
     * Returns true if the gap between this entry's end and the subsequent entry's start is 10 hours or more.
     * Returns false when no subsequent entry exists — absence of a following entry is not considered a gap.
     *
     * @param timeRecordingEntry the entry whose end time is being checked
     * @param subsequentEntry the entry immediately after `timeRecordingEntry`, or undefined if none exists
     * @returns true if the gap to the subsequent entry is 10 or more hours
     */
    public isEntryFollowedByGapOf10HoursOrMore(
        timeRecordingEntry: TimeRecordingEntryModel,
        subsequentEntry: TimeRecordingEntryModel | undefined,
    ): boolean {
        const gapHours = 10;
        return typeof subsequentEntry !== 'undefined'
            ? subsequentEntry.startDateTime.diff(timeRecordingEntry.endDateTime, 'hours') >= gapHours
            : false;
    }

    /**
     * Determines whether the time gap between `timeRecordingEntry` and its subsequent entry is unoccupied.
     *
     * A gap exists when the subsequent entry starts after `timeRecordingEntry` ends. The gap is considered
     * occupied only if `otherEntries` (e.g. break or absence entries) cover it without any holes:
     *   - Single entry: one other entry spans exactly from the gap start to the gap end.
     *   - Multiple entries: a contiguous chain of other entries covers the full gap with no internal gaps.
     *
     * @param otherEntries - Entries of a different type (e.g. breaks) that may fill the gap.
     * @param timeRecordingEntry - The entry whose trailing gap is being evaluated.
     * @param subsequentEntry - The entry immediately after `timeRecordingEntry`, or undefined if none exists.
     * @returns `true` if there is a gap and it is not fully covered by `otherEntries`; `false` otherwise.
     */
    public isEntriesHavingAnUnoccupiedGap(
        otherEntries: TimesheetEntryInterface[],
        timeRecordingEntry: TimeRecordingEntryModel,
        subsequentEntry: TimeRecordingEntryModel | undefined,
    ): boolean {
        const isGap =
            typeof subsequentEntry !== 'undefined' &&
            subsequentEntry.startDateTime.diff(timeRecordingEntry.endDateTime, 'minutes') > 0;
        if (isGap && otherEntries.length > 0) {
            // check if gap is occupied by single entry
            const isGapOccupied = otherEntries.some(
                (entry) =>
                    entry.startTime.value.diff(timeRecordingEntry.endDateTime, 'minutes') === 0 &&
                    entry.endTime.value.diff(subsequentEntry?.startDateTime, 'minutes') === 0,
            );
            // check if gap is occupied by multiple entries
            if (!isGapOccupied && otherEntries.length > 1) {
                const entryStartingAtSameTime = otherEntries.find(
                    (entry) =>
                        entry.startTime.value.diff(timeRecordingEntry.endDateTime, 'minutes') === 0 &&
                        entry.endTime.value.diff(entry.startTime.value, 'minutes') > 0,
                );
                const entryEndingAtSameTime = otherEntries.find(
                    (entry) =>
                        typeof subsequentEntry !== 'undefined' &&
                        entry.endTime.value.diff(subsequentEntry.startDateTime, 'minutes') === 0 &&
                        entry.endTime.value.diff(entry.startTime.value, 'minutes') > 0,
                );
                if (typeof entryStartingAtSameTime !== 'undefined' && typeof entryEndingAtSameTime !== 'undefined') {
                    const startEntryIndex = otherEntries.findIndex(
                        (currentEntry) => currentEntry === entryStartingAtSameTime,
                    );
                    const endEntryIndex = otherEntries.findIndex(
                        (currentEntry) => currentEntry === entryEndingAtSameTime,
                    );
                    // get all entries between the entryStartingAtSameTime and entryEndingAtSameTime
                    const subsequentEntries = otherEntries.slice(startEntryIndex, endEntryIndex + 1);

                    let previousEntry: TimesheetEntryInterface | undefined;
                    let isGapBetweenEntries = false;
                    for (const entry of subsequentEntries) {
                        if (previousEntry && entry.startTime.value.diff(previousEntry.endTime.value) > 0) {
                            isGapBetweenEntries = true;
                            break;
                        }
                        // update previous entry
                        previousEntry = entry;
                    }
                    return isGapBetweenEntries;
                } else {
                    return true;
                }
            }
            return !isGapOccupied;
        }
        return isGap;
    }

    /// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // NEW ENTRY VALIDATION ////////////////////////////////////////////////////////////////////////////////////////////
    /// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    public performNewEntryValidation(
        timeRecordingEntries: TimeRecordingEntryModel[],
        newEntry: TimeRecordingGeneralEntryModel,
        isRemoteWork = false,
    ): TimeRecordingValidationResultModel[] {
        let validationResultArray: TimeRecordingValidationResultModel[] = [];

        validationResultArray = validationResultArray.concat(
            this.checkTypeOfWork(
                newEntry,
                <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_NEW_ENTRY_NO_TYPEOFWORK'),
            ),
        );
        validationResultArray = validationResultArray.concat(
            this.checkActivity(
                newEntry,
                <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_NEW_ENTRY_NO_ACTIVITY'),
            ),
        );
        if (!isRemoteWork) {
            const travelToEntries = this.timeRecordingService.getTravelToEntries(timeRecordingEntries);
            // If standby travel is enabled, check if new entry starts before last travel-to entry
            const entryTRTO = this.configService.getEnableStandbyTravel()
                ? travelToEntries[travelToEntries.length - 1]
                : this.timeRecordingService.getFirstTimeRecordingEntry(timeRecordingEntries);
            validationResultArray = validationResultArray.concat(
                this.checkIfNewEntryStartsBeforeTRTO(
                    newEntry,
                    entryTRTO,
                    <string>(
                        this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_NEW_ENTRY_STARTS_BEFORE_TRTO')
                    ),
                ),
            );
            const travelBackEntries = this.timeRecordingService.getTravelBackEntries(timeRecordingEntries);
            validationResultArray = validationResultArray.concat(
                this.checkIfNewEntryStartsOrEndsLaterThanTRBK(
                    newEntry,
                    travelBackEntries,
                    <string>(
                        this.translate.instant(
                            'CONFIRMATION_TIMERECORDING_VALIDATION_NEW_ENTRY_STARTS_OR_ENDS_AFTER_TRBK',
                        )
                    ),
                ),
            );
        }
        validationResultArray = validationResultArray.concat(
            this.checkIfNewEntryEndTimeIsBeforeNewEntryStartTime(
                newEntry,
                <string>this.translate.instant(
                    'CONFIRMATION_TIMERECORDING_VALIDATION_NEW_ENTRY_END_TIME_BEFORE_NEW_ENTRY_START_TIME',
                    {
                        endTime: newEntry.endDateTime.format(this.clientManager.getDateTimeFormat(true)),
                        startTime: newEntry.startDateTime.format(this.clientManager.getDateTimeFormat(true)),
                    },
                ),
            ),
        );
        validationResultArray = validationResultArray.concat(
            this.checkIfNewEntryHasDuration(
                newEntry,
                <string>this.translate.instant('CONFIRMATION_TIMERECORDING_VALIDATION_NEW_ENTRY_NO_DURATION'),
            ),
        );

        return validationResultArray;
    }

    public checkIfNewEntryHasDuration(
        newEntry: TimeRecordingGeneralEntryModel,
        message: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        const validity = newEntry.endDateTime.isAfter(newEntry.startDateTime);
        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.NEW_ENTRY_OSR_HAS_DURATION,
            message,
            validity,
            newEntry.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    public checkIfNewEntryEndTimeIsBeforeNewEntryStartTime(
        newEntry: TimeRecordingGeneralEntryModel,
        message: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        const validity: boolean = newEntry.endDateModel.value.isSameOrAfter(newEntry.startDateModel.value);
        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.NEW_ENTRY_OSR_HAS_DURATION,
            message,
            validity,
            newEntry.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    public checkTypeOfWork(
        newEntry: TimeRecordingGeneralEntryModel,
        message: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        let validity = true;

        if (!newEntry.getTypeOfWorkCode()) {
            validity = false;
        }

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.NEW_ENTRY_NO_TYPEOFWORK,
            message,
            validity,
            newEntry.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    public checkActivity(
        newEntry: TimeRecordingGeneralEntryModel,
        message: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        let validity = true;

        if (
            newEntry.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.OSR &&
            !newEntry.activitiesModel.selectedEntry
        ) {
            validity = false;
        }

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.NEW_ENTRY_NO_ACTIVITY,
            message,
            validity,
            newEntry.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    public checkIfNewEntryStartsBeforeTRTO(
        newEntry: TimeRecordingGeneralEntryModel,
        entryTRTO: TimeRecordingEntryModel | undefined,
        message: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        let validity = true;

        if (!entryTRTO) {
            throw new Error(`Could not find TRTO entry!`);
        }

        if (newEntry.startDateTime.isBefore(entryTRTO.startDateTime)) {
            validity = false;
        }

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.NEW_ENTRY_STARTS_BEFORE_TRTO,
            message,
            validity,
            newEntry.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    public checkIfNewEntryStartsOrEndsLaterThanTRBK(
        newEntry: TimeRecordingGeneralEntryModel,
        travelBackEntries: TimeRecordingEntryModel[],
        message: string,
    ): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];
        let validity = true;

        if (travelBackEntries.length === 0) {
            throw new Error(`Could not find TRBK entry!`);
        }

        const sortedTRBKs = [...travelBackEntries].sort((a, b) => (a.startDateTime.isBefore(b.startDateTime) ? -1 : 1));
        const firstTRBK = sortedTRBKs[0];
        const lastTRBK = sortedTRBKs[sortedTRBKs.length - 1];

        // Break entries may start/end anywhere up to the last TRBK's end time;
        // all other entry types must not start after the first TRBK begins.
        const isInvalid = newEntry.isBreakEntry()
            ? newEntry.startDateTime.isAfter(lastTRBK.endDateTime) || newEntry.endDateTime.isAfter(lastTRBK.endDateTime)
            : newEntry.startDateTime.isAfter(firstTRBK.startDateTime);

        if (isInvalid && !newEntry.isPhoneFix()) {
            validity = false;
        }

        const validationResultEntry = new TimeRecordingValidationResultModel(
            TIMERECORDING_ENTRY_VALIDITY.NEW_ENTRY_STARTS_OR_ENDS_AFTER_TRBK,
            message,
            validity,
            newEntry.id,
        );
        validationResultArray.push(validationResultEntry);

        return validationResultArray;
    }

    public checkStartTime(timeRecordingEntries: TimeRecordingEntryModel[]): TimeRecordingValidationResultModel[] {
        const validationResultArray: TimeRecordingValidationResultModel[] = [];

        timeRecordingEntries.forEach((currentEntry: TimeRecordingEntryModel) => {
            const osrLimitValueHelper = this.valueHelperService.getOsrLimitValueHelper();
            const osrLimitFuture = osrLimitValueHelper ? moment.duration(osrLimitValueHelper.descText) : undefined;
            const now = moment();

            const validationResultEntry = new TimeRecordingValidationResultModel(
                TIMERECORDING_VALIDITY.INVALID_FUTURE_START_TIME,
                <string>this.translate.instant(
                    'CONFIRMATION_TIMERECORDING_VALIDATION_MAXIMUM_ALLOWED_STARTTIME_IN_FUTURE',
                    {
                        startTime: currentEntry.startDateTime.format(this.clientManager.getDateTimeFormat()),
                        maxAllowedFutureTime: now
                            .clone()
                            .add(osrLimitFuture)
                            .format(this.clientManager.getDateTimeFormat()),
                    },
                ),
                true,
                currentEntry.id,
            );
            validationResultArray.push(validationResultEntry);

            if (currentEntry.startDateTime.isAfter(now.clone().add(osrLimitFuture))) {
                validationResultEntry.isValid = false;
            }
        });

        return validationResultArray;
    }
}
